import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Trash2 } from "lucide-react";
import { adminApi, type ProfileRow, type Role } from "@/lib/api";

const ROLE_LABEL: Record<Role, string> = { customer: "お客様", vendor: "業者", admin: "管理者" };
const ROLE_STYLE: Record<Role, string> = {
  customer: "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200",
  vendor: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200",
  admin: "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200",
};
const ROLES: Role[] = ["customer", "vendor", "admin"];

export function AccountsPage() {
  const qc = useQueryClient();
  const [myId, setMyId] = useState<string | null>(null);
  const [filter, setFilter] = useState<"" | Role>("");
  useEffect(() => {
    adminApi.myId().then(setMyId);
  }, []);

  const { data: profiles, isLoading, error } = useQuery({ queryKey: ["profiles"], queryFn: () => adminApi.listProfiles() });

  const roleMut = useMutation({
    mutationFn: ({ id, role }: { id: string; role: Role }) => adminApi.setProfileRole(id, role),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["profiles"] }),
  });
  const delMut = useMutation({
    mutationFn: (id: string) => adminApi.deleteAccount(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["profiles"] }),
  });

  const list = (profiles ?? []).filter((p) => !filter || p.role === filter);

  return (
    <div className="px-4 py-4 space-y-3">
      <h2 className="text-lg font-semibold">アカウント管理</h2>
      <p className="text-xs text-ink-soft dark:text-white/60">ロール変更・アカウント削除ができます。自分自身は変更・削除できません。</p>

      <div className="flex gap-2 flex-wrap text-sm">
        <button onClick={() => setFilter("")} className={`px-3 py-1 rounded-full border ${filter === "" ? "bg-brand-500 text-white border-brand-500" : "border-[var(--color-border)]"}`}>全員</button>
        {ROLES.map((r) => (
          <button key={r} onClick={() => setFilter(r)} className={`px-3 py-1 rounded-full border ${filter === r ? "bg-brand-500 text-white border-brand-500" : "border-[var(--color-border)]"}`}>
            {ROLE_LABEL[r]}
          </button>
        ))}
      </div>

      {isLoading && <p className="text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> 読み込み中...</p>}
      {error && <p className="text-sm text-red-600">読み込みに失敗しました</p>}
      {delMut.error && <p className="text-sm text-red-600">{(delMut.error as Error).message}</p>}
      {profiles && list.length === 0 && <p className="text-sm text-ink-soft">該当するアカウントがありません。</p>}

      <ul className="space-y-2">
        {list.map((p: ProfileRow) => {
          const self = p.id === myId;
          return (
            <li key={p.id} className="rounded-xl border border-[var(--color-border)] p-3 sm:p-4 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${ROLE_STYLE[p.role]}`}>{ROLE_LABEL[p.role]}</span>
                  <span className="font-medium truncate">{p.name || "（氏名未設定）"}</span>
                  {self && <span className="text-[10px] text-ink-soft">（自分）</span>}
                </div>
                <p className="text-xs text-ink-soft dark:text-white/60 truncate mt-1">{p.email || "メール未登録"}</p>
                <p className="text-[11px] text-ink-soft dark:text-white/40">{p.prefecture || ""} ／ {new Date(p.created_at).toLocaleDateString("ja-JP")}</p>
              </div>
              <div className="flex flex-col items-end gap-2 shrink-0">
                <select
                  value={p.role}
                  disabled={self || roleMut.isPending}
                  onChange={(e) => roleMut.mutate({ id: p.id, role: e.target.value as Role })}
                  className="text-xs border border-[var(--color-border)] rounded-md bg-[var(--color-bg)] px-2 py-1 disabled:opacity-50"
                >
                  {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
                </select>
                <button
                  onClick={() => {
                    if (window.confirm(`${p.email || p.name || "このアカウント"} を完全に削除します。元に戻せません。よろしいですか？`)) delMut.mutate(p.id);
                  }}
                  disabled={self || delMut.isPending}
                  className="p-1.5 rounded-md border border-red-300 text-red-600 disabled:opacity-40"
                  aria-label="削除"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
